import { Drawer, GradeRangeSelector } from '@/components';
import { BodyLargeEmphasized, ButtonFlatMedium, ButtonPrimaryMedium } from '@/design-system';
import { css } from '@emotion/css';
import colors from '@/theme/colors';
import { Grade } from '@/pages/AccountInformation/AccountInformation.types';
import { Divider } from '../Divider';
import { FilterOption } from './FilterOption';
import type { OptionType } from './FilterOption';

type Props = {
  isOpen: boolean;
  title: string;
  viewResultsLabel: string;
  onClearFilters: () => void;
  onShowResults: () => void;
  onToggle: (state: boolean) => void;
  onSelectTypesToggle: (value: string, description: string) => () => void;
  disableClearAllButton: boolean;
  flyerType: string;
  types: OptionType[];
  selectedTypes: string[];
  grades: Grade[];
  gradeFilter: { from?: string; to?: string };
  initialGradeValue?: { from?: string; to?: string };
  handleGradeChange: (field: 'from' | 'to', value?: string) => void;
  facetsLoading: boolean;
};

export const DrawerFilter: React.FC<Props> = ({
  isOpen,
  title,
  viewResultsLabel,
  onClearFilters,
  onShowResults,
  disableClearAllButton,
  onToggle,
  selectedTypes,
  onSelectTypesToggle,
  types,
  grades,
  gradeFilter,
  handleGradeChange,
  facetsLoading,
  initialGradeValue,
}) => {
  return (
    <Drawer title={title} isOpen={isOpen} onToggle={onToggle} full>
      <div className={styles.container}>
        <div className={styles.filterList}>
          <fieldset className={styles.contentFieldset}>
            <legend className={styles.legend}>
              <BodyLargeEmphasized className={styles.filterName} id="filter-grades-label">
                Grades
              </BodyLargeEmphasized>
            </legend>
            <div className={styles.optionsContainer}>
              <GradeRangeSelector
                fromGrade={gradeFilter.from}
                toGrade={gradeFilter.to}
                onChange={handleGradeChange}
                grades={grades}
                initialValue={initialGradeValue}
              />
            </div>
            <div className={styles.divider}>
              <Divider />
            </div>
          </fieldset>
          <fieldset className={styles.contentFieldset}>
            <legend className={styles.legend}>
              <BodyLargeEmphasized className={styles.filterName} id="filter-types-label">
                Types
              </BodyLargeEmphasized>
            </legend>
            <div className={styles.optionsContainer}>
              {types.map(({ value, description, disabled }) => (
                <FilterOption
                  key={value}
                  description={description}
                  value={value}
                  filterName="types"
                  checked={selectedTypes.includes(value)}
                  handleToggle={() => onSelectTypesToggle(value, description)}
                  disabled={disabled && !selectedTypes.includes(value)}
                />
              ))}
            </div>
          </fieldset>
        </div>

        <div className={styles.footer}>
          <ButtonFlatMedium
            data-testid="clearAllButton"
            onClick={onClearFilters}
            disabled={disableClearAllButton}
          >
            Reset Filters
          </ButtonFlatMedium>
          <ButtonPrimaryMedium
            onClick={onShowResults}
            data-testid="showResultsButton"
            loading={facetsLoading}
            nonTranslate
          >
            {viewResultsLabel}
          </ButtonPrimaryMedium>
        </div>
      </div>
    </Drawer>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-flow: column nowrap;
    flex: 1;
  `,
  filterList: css`
    flex-grow: 1;
  `,
  footer: css`
    box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    position: sticky;
    background-color: ${colors.white};
    z-index: 1199;
    bottom: 0;
    padding: 24px;
    margin-top: 25px;
    justify-content: space-between;
    width: 100%;
  `,
  contentFieldset: css`
    padding: 16px 16px 0 16px;
    border: 0;
    margin: 0;
    min-inline-size: 0;
    padding-block: 0;
  `,
  legend: css`
    margin: 0;
    padding: 16px 0px 0px;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  optionsContainer: css`
    padding-top: 7px;
  `,
  divider: css`
    padding: 15px 0 0 0;
  `,
};
